import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import { IconButton } from '@mui/material';
import { Dropdown, MenuProps } from 'antd';
import dummyAvatar from 'assets/imgs/dummy-avatar.jpg';
import appLogoDark from 'assets/logos/logoDark.png';
import appLogoLight from 'assets/logos/logoLight.png';
import clsx from 'clsx';
import ScrollRestoration from 'components/ScrollRestoration';
import SplashScreen from 'components/SplashScreen';
import { darkModeSelector } from 'features/appUI/selectors';
import { setSelectedMenuKeys } from 'features/appUI/slice';
import { clearAuthToken } from 'features/auth/helpers';
import { currentUserSelector } from 'features/auth/selectors';
import { clearAuth } from 'features/auth/slice';
import AppMainSidebar from 'layouts/MainLayout/AppMainSidebar/AppMainSidebar';
import { baseApiEndpoint } from 'lib/constants';
import { baseApi } from 'lib/rtkQuery/baseApi';
import { useAppDispatch, useAppSelector } from 'lib/stores/hook';
import { memo, Suspense, useCallback, useMemo } from 'react';
import { useAuth } from 'react-oidc-context';
import { Outlet, useNavigate } from 'react-router-dom';
import { getPath } from 'routes/router-paths';
import { capitalizeFirstLetterOfEachWord } from 'utils/dataTypes/string';

const MainLayout = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const auth = useAuth();

  const currentUser = useAppSelector(currentUserSelector);
  const isDarkModeEnabled = useAppSelector(darkModeSelector);

  const avatarUrl = currentUser?.image_id
    ? `${baseApiEndpoint}/images/${currentUser.image_id}`
    : undefined;

  const userMenuItems = useMemo(
    (): MenuProps['items'] => [
      {
        label: 'Thông tin cá nhân',
        icon: <AccountCircleIcon />,
        key: 'personal',
      },
      {
        label: 'Đăng xuất',
        icon: <LogoutOutlinedIcon />,
        key: 'logout',
        danger: true,
      },
    ],
    []
  );

  const onUserMenuClick: MenuProps['onClick'] = useCallback(
    async ({ key }: { key: string }) => {
      if (key === 'logout') {
        clearAuthToken();
        dispatch(clearAuth());
        dispatch(baseApi.util.resetApiState());
        await auth.signoutRedirect();
      }
    },
    [auth, dispatch]
  );

  return (
    <div className="flex bg-[#F2F2F2]" role="main">
      <aside className="relative z-10 h-screen w-80 shrink-0 p-4">
        <div
          className="flex h-full flex-col items-center rounded-2xl bg-white"
          style={{
            boxShadow: 'var(--wrapper-shadow)',
          }}
        >
          <div className="flex w-full items-center justify-between gap-x-10 pb-5 pl-5 pr-3 pt-5">
            <figure
              className="flex cursor-pointer items-center"
              onClick={() => {
                navigate(getPath('dashboardPage'));
                dispatch(setSelectedMenuKeys([getPath('dashboardPage')]));
              }}
            >
              <img
                src={isDarkModeEnabled ? appLogoDark : appLogoLight}
                alt="App Logo"
                className="block size-14"
              />
            </figure>

            <IconButton>
              <NotificationsNoneOutlinedIcon className="!text-2xl" />
            </IconButton>
          </div>

          <Dropdown
            menu={{ items: userMenuItems, onClick: onUserMenuClick }}
            placement="bottom"
            overlayClassName="user-menu-dropdown"
            trigger={['click']}
          >
            <div
              className={clsx(
                'flex flex-col items-center',
                'mb-4 cursor-pointer rounded-full px-2 py-1'
              )}
            >
              <img
                src={avatarUrl || dummyAvatar || '/placeholder.svg'}
                alt="User avatar"
                className="size-24 rounded-full border border-gray-200 object-cover dark:border-gray-600"
              />

              <span className="-translate-y-1/2 rounded-xl bg-primary px-2.5 py-1 text-center text-sm font-medium text-white">
                {capitalizeFirstLetterOfEachWord(
                  currentUser?.name_uppercase ?? ''
                )}
              </span>
            </div>
          </Dropdown>

          <div className="w-full flex-1 overflow-y-auto overflow-x-hidden px-4 pb-4">
            <AppMainSidebar />
          </div>
        </div>
      </aside>

      <main
        className="flex h-screen w-full flex-col overflow-y-auto overflow-x-hidden p-4 pl-3.5 pr-7.5"
        role="main"
      >
        <div className="mx-auto w-full max-w-[160rem]">
          <Suspense
            fallback={
              <ScrollRestoration>
                <SplashScreen fullScreen />
              </ScrollRestoration>
            }
          >
            <Outlet />
          </Suspense>
        </div>
      </main>
    </div>
  );
};

export default memo(MainLayout);
